import { useState, useMemo } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Chip,
  Dialog,
  DialogContent,
  ListItemButton,
  ListItemText,
  TextField,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  KeyboardShortcutCategory,
  useCommandPaletteDialogShortcuts,
  useRegisteredShortcuts,
} from 'hooks/useKeyboardShortcuts';
import { useTranslation } from 'react-i18next';
import { watchdutyDarkTheme } from 'theme';

const categoryOrder: Array<KeyboardShortcutCategory> = [
  'common',
  'baseMap',
  'mapLayers',
  'mapTools',
  'camera',
];

type CommandOption = {
  groupBy: string;
  label: string;
  handler: () => void;
  render: (props: React.HTMLAttributes<HTMLLIElement>) => JSX.Element;
};

export const CommandPaletteDialog = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();
  const shortcuts = useRegisteredShortcuts();

  useCommandPaletteDialogShortcuts(() => {
    setIsOpen((prev) => !prev);
  });

  const shortcutCommands: CommandOption[] = useMemo(() => {
    return shortcuts
      .filter(
        (shortcut) =>
          // prevent the command palette from showing up in the list
          shortcut.description !== t('keyboardShortcuts.openCommandPalette'),
      )
      .sort((a, b) => {
        // sort by the grouping
        if (a.category === b.category) {
          return a.description.localeCompare(b.description);
        }
        return (
          categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)
        );
      })
      .map((shortcut) => {
        return {
          groupBy: t(`keyboardShortcuts.categories.${shortcut.category}`),
          label: shortcut.description,
          handler: shortcut.handler,
          render: (props) => (
            <ListItemButton component="li" {...props}>
              <ListItemText primary={shortcut.description} />
              <Chip label={shortcut.keys} variant="keyboard-shortcut" />
            </ListItemButton>
          ),
        };
      });
  }, [shortcuts, t]);

  const handleSelection = (command: CommandOption | null): void => {
    if (command) {
      setIsOpen(false);
      command.handler();
    }
  };

  const transparentPaperSlotProps = {
    paper: {
      style: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
      },
    },
  };

  return (
    <ThemeProvider theme={watchdutyDarkTheme}>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        sx={{
          // Because autocomplete takes up room, move dialog to top ~1/4 of screen
          marginTop: 20,
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        }}
        slotProps={transparentPaperSlotProps}
        fullWidth
      >
        {shortcutCommands.length > 0 && (
          <DialogContent>
            <Autocomplete
              slotProps={transparentPaperSlotProps}
              fullWidth
              autoHighlight
              openOnFocus
              options={shortcutCommands}
              clearOnEscape
              onChange={(_event, command) => handleSelection(command)}
              getOptionLabel={(command) => command.label}
              groupBy={(command) => command.groupBy}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                  {...params}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
                  onBlur={() => setIsOpen(false)}
                />
              )}
              renderOption={(props, option) => option.render(props)}
            />
          </DialogContent>
        )}
      </Dialog>
    </ThemeProvider>
  );
};
